/**
 * Typed API client — exactly one function per endpoint in `shared/api.ts`.
 *
 * Every call is same-origin with `credentials: 'include'` (the session cookie is
 * HttpOnly) and carries `X-Workspace-Id` for workspace-scoped routes. Failures
 * throw `ApiError`, so react-query surfaces them without per-call error checks.
 *
 *   const { posts } = await api.posts.list({ status: 'scheduled' });
 *
 * Owned by Scaffold/Integration. If something is missing, report it rather than
 * editing this file from a feature agent.
 */

import type {
  AcceptInviteBody,
  AcceptInviteResponse,
  AdminActivityQuery,
  AdminActivityResponse,
  AdminAiUsageQuery,
  AdminAiUsageResponse,
  AdminOverviewResponse,
  AdminUsersQuery,
  AdminUsersResponse,
  AiAltTextBody,
  AiAltTextResponse,
  AiHashtagsBody,
  AiHashtagsResponse,
  AiIdeasBody,
  AiIdeasResponse,
  AiImageBody,
  AiImageResponse,
  AiImproveBody,
  AiImproveResponse,
  AnalyzeStyleBody,
  AnalyzeStyleResponse,
  ApiErrorBody,
  ApiFieldError,
  CalendarQuery,
  CalendarResponse,
  ConnectAccountResponse,
  ConnectWordPressBody,
  ConnectWordPressResponse,
  CreateInviteBody,
  CreateInviteResponse,
  CreatePostBody,
  CreatePostResponse,
  CreateRenditionBody,
  CreateRenditionResponse,
  CreateSimulatedAccountBody,
  CreateSimulatedAccountResponse,
  CreateWorkspaceBody,
  CreateWorkspaceResponse,
  CreateWritingSampleBody,
  CreateWritingSampleResponse,
  DeleteInviteResponse,
  DeleteMediaResponse,
  DeletePostResponse,
  DeleteWritingSampleResponse,
  DevLoginBody,
  DevLoginResponse,
  DevSeedBody,
  DevSeedResponse,
  DisconnectAccountResponse,
  GeneratePostBody,
  GeneratePostResponse,
  GetBusinessProfileResponse,
  GetMediaResponse,
  GetPostResponse,
  GetStyleProfileResponse,
  GetWorkspaceResponse,
  LearnFromEditBody,
  LearnFromEditResponse,
  ListAccountsResponse,
  ListInvitesResponse,
  ListMediaQuery,
  ListMediaResponse,
  ListMembersResponse,
  ListNotificationsQuery,
  ListNotificationsResponse,
  ListPostsQuery,
  ListPostsResponse,
  ListWorkspacesResponse,
  ListWritingSamplesResponse,
  LogoutResponse,
  LookupInviteResponse,
  MagicLinkRequestBody,
  MagicLinkRequestResponse,
  MagicLinkVerifyQuery,
  MagicLinkVerifyResponse,
  MarkNotificationsReadBody,
  MarkNotificationsReadResponse,
  MeResponse,
  PublishAllResponse,
  PublishPlatformPostResponse,
  RegeneratePlatformPostBody,
  RegeneratePlatformPostResponse,
  RemoveMemberResponse,
  SchedulePostBody,
  SchedulePostResponse,
  SetupBody,
  SetupResponse,
  SetupStateResponse,
  UpdateBusinessProfileBody,
  UpdateBusinessProfileResponse,
  UpdateMediaBody,
  UpdateMediaResponse,
  UpdateMemberBody,
  UpdateMemberResponse,
  UpdatePlatformPostBody,
  UpdatePlatformPostResponse,
  UpdatePostBody,
  UpdatePostResponse,
  UpdateStyleProfileBody,
  UpdateStyleProfileResponse,
  UpdateWorkspaceBody,
  UpdateWorkspaceResponse,
  UploadMediaResponse,
} from '../../shared/api';
import type { PlatformId } from '../../shared/types';
import { buildQuery, mediaFilePath } from '../../shared/utils';

/* ------------------------------------------------------------------ */
/* Errors                                                              */
/* ------------------------------------------------------------------ */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: ApiFieldError[];
  readonly requestId?: string;

  constructor(status: number, body: Partial<ApiErrorBody> & { message?: string }) {
    super(body.message || 'Something went wrong.');
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error ?? 'internal_error';
    this.fields = body.fields ?? [];
    if (body.requestId) this.requestId = body.requestId;
  }

  /** Convenience for forms: field path → message. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.fields.map((field) => [field.path, field.message]));
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotImplemented(): boolean {
    return this.status === 501;
  }
}

/* ------------------------------------------------------------------ */
/* Workspace scoping + 401 handling                                    */
/* ------------------------------------------------------------------ */

let activeWorkspaceId: string | null = null;
const unauthorizedHandlers = new Set<() => void>();

/** Called by the workspace store whenever the active workspace changes. */
export function setActiveWorkspaceId(workspaceId: string | null): void {
  activeWorkspaceId = workspaceId;
}

export function getActiveWorkspaceId(): string | null {
  return activeWorkspaceId;
}

/** Register a callback for 401s (the session store clears state and redirects). */
export function onUnauthorized(handler: () => void): () => void {
  unauthorizedHandlers.add(handler);
  return () => unauthorizedHandlers.delete(handler);
}

/* ------------------------------------------------------------------ */
/* Core request helper                                                 */
/* ------------------------------------------------------------------ */

type QueryValue = string | number | boolean | null | undefined;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  form?: FormData;
  query?: Record<string, QueryValue>;
  /** Override the active workspace for this call. */
  workspaceId?: string | null;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, form, query, workspaceId, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  const scope = workspaceId === undefined ? activeWorkspaceId : workspaceId;
  if (scope) headers['X-Workspace-Id'] = scope;

  let payload: BodyInit | undefined;
  if (form) {
    payload = form;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const response = await fetch(`${path}${query ? buildQuery(query) : ''}`, {
    method,
    headers,
    body: payload,
    credentials: 'include',
    signal: signal ?? null,
  });

  if (response.status === 401) {
    for (const handler of unauthorizedHandlers) handler();
  }

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('Content-Type') ?? '';
  const isJson = contentType.includes('application/json');
  const data: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    throw new ApiError(
      response.status,
      isJson && data && typeof data === 'object'
        ? (data as ApiErrorBody)
        : { error: 'internal_error', message: typeof data === 'string' && data ? data : undefined },
    );
  }

  return data as T;
}

/* ------------------------------------------------------------------ */
/* Client                                                              */
/* ------------------------------------------------------------------ */

export const api = {
  /* ---------------- auth ---------------- */
  auth: {
    /** GET /api/auth/me — bootstrap: user, memberships, workspaces, auth methods. */
    me: (signal?: AbortSignal) => request<MeResponse>('/api/auth/me', { signal }),

    /** GET /api/auth/setup-state */
    setupState: () => request<SetupStateResponse>('/api/auth/setup-state'),

    /** POST /api/auth/magic-link/request */
    requestMagicLink: (body: MagicLinkRequestBody) =>
      request<MagicLinkRequestResponse>('/api/auth/magic-link/request', { method: 'POST', body }),

    /**
     * GET /api/auth/magic-link/verify — a browser navigation, not a fetch.
     * `window.location.href = api.auth.magicLinkVerifyUrl(token)`
     */
    magicLinkVerifyUrl: (token: string, redirect?: string) =>
      `/api/auth/magic-link/verify${buildQuery({ token, redirect })}`,

    /** POST /api/auth/magic-link/verify — consumes the token from /auth/continue. */
    verifyMagicLink: (body: MagicLinkVerifyQuery) =>
      request<MagicLinkVerifyResponse>('/api/auth/magic-link/verify', { method: 'POST', body }),

    /** GET /api/auth/google — navigate here to start Google sign-in. */
    googleStartUrl: (redirect?: string, inviteToken?: string) =>
      `/api/auth/google${buildQuery({ redirect, inviteToken })}`,

    /**
     * GET /api/auth/google/callback — Google redirects the browser here; the
     * Worker sets the session cookie and 302s back into the app. Exposed so the
     * OAuth client configuration and the app stay in sync.
     */
    googleCallbackPath: () => '/api/auth/google/callback',

    /** POST /api/auth/setup — first-run superadmin claim. */
    setup: (body: SetupBody) =>
      request<SetupResponse>('/api/auth/setup', { method: 'POST', body }),

    /** POST /api/auth/dev-login — DEV_MODE only. */
    devLogin: (body: DevLoginBody) =>
      request<DevLoginResponse>('/api/auth/dev-login', { method: 'POST', body }),

    /** POST /api/auth/logout */
    logout: () => request<LogoutResponse>('/api/auth/logout', { method: 'POST' }),
  },

  /* ---------------- workspaces, members, invites ---------------- */
  workspaces: {
    /** GET /api/workspaces */
    list: () => request<ListWorkspacesResponse>('/api/workspaces'),

    /** POST /api/workspaces */
    create: (body: CreateWorkspaceBody) =>
      request<CreateWorkspaceResponse>('/api/workspaces', { method: 'POST', body }),

    /** GET /api/workspaces/:id */
    get: (workspaceId: string) =>
      request<GetWorkspaceResponse>(`/api/workspaces/${workspaceId}`),

    /** PATCH /api/workspaces/:id */
    update: (workspaceId: string, body: UpdateWorkspaceBody) =>
      request<UpdateWorkspaceResponse>(`/api/workspaces/${workspaceId}`, {
        method: 'PATCH',
        body,
      }),

    /** GET /api/workspaces/:id/members */
    members: (workspaceId: string) =>
      request<ListMembersResponse>(`/api/workspaces/${workspaceId}/members`),

    /** PATCH /api/workspaces/:id/members/:userId */
    updateMember: (workspaceId: string, userId: string, body: UpdateMemberBody) =>
      request<UpdateMemberResponse>(`/api/workspaces/${workspaceId}/members/${userId}`, {
        method: 'PATCH',
        body,
      }),

    /** DELETE /api/workspaces/:id/members/:userId */
    removeMember: (workspaceId: string, userId: string) =>
      request<RemoveMemberResponse>(`/api/workspaces/${workspaceId}/members/${userId}`, {
        method: 'DELETE',
      }),

    /** GET /api/workspaces/:id/invites */
    invites: (workspaceId: string) =>
      request<ListInvitesResponse>(`/api/workspaces/${workspaceId}/invites`),

    /** POST /api/workspaces/:id/invites */
    createInvite: (workspaceId: string, body: CreateInviteBody) =>
      request<CreateInviteResponse>(`/api/workspaces/${workspaceId}/invites`, {
        method: 'POST',
        body,
      }),

    /** DELETE /api/workspaces/:id/invites/:inviteId */
    deleteInvite: (workspaceId: string, inviteId: string) =>
      request<DeleteInviteResponse>(`/api/workspaces/${workspaceId}/invites/${inviteId}`, {
        method: 'DELETE',
      }),

    /** GET /api/workspaces/:id/business-profile */
    businessProfile: (workspaceId: string) =>
      request<GetBusinessProfileResponse>(`/api/workspaces/${workspaceId}/business-profile`),

    /** PUT /api/workspaces/:id/business-profile */
    saveBusinessProfile: (workspaceId: string, body: UpdateBusinessProfileBody) =>
      request<UpdateBusinessProfileResponse>(`/api/workspaces/${workspaceId}/business-profile`, {
        method: 'PUT',
        body,
      }),
  },

  invites: {
    /** GET /api/invites/lookup?token= — public preview for the accept page. */
    lookup: (token: string) =>
      request<LookupInviteResponse>('/api/invites/lookup', { query: { token } }),

    /** POST /api/invites/accept */
    accept: (body: AcceptInviteBody) =>
      request<AcceptInviteResponse>('/api/invites/accept', { method: 'POST', body }),
  },

  /* ---------------- writing style ---------------- */
  style: {
    /** GET /api/style/samples */
    samples: () => request<ListWritingSamplesResponse>('/api/style/samples'),

    /** POST /api/style/samples */
    addSample: (body: CreateWritingSampleBody) =>
      request<CreateWritingSampleResponse>('/api/style/samples', { method: 'POST', body }),

    /** DELETE /api/style/samples/:id */
    deleteSample: (sampleId: string) =>
      request<DeleteWritingSampleResponse>(`/api/style/samples/${sampleId}`, {
        method: 'DELETE',
      }),

    /** POST /api/style/analyze */
    analyze: (body: AnalyzeStyleBody = {}) =>
      request<AnalyzeStyleResponse>('/api/style/analyze', { method: 'POST', body }),

    /** GET /api/style/profile */
    profile: () => request<GetStyleProfileResponse>('/api/style/profile'),

    /** PUT /api/style/profile */
    saveProfile: (body: UpdateStyleProfileBody) =>
      request<UpdateStyleProfileResponse>('/api/style/profile', { method: 'PUT', body }),

    /** POST /api/style/learn */
    learnFromEdit: (body: LearnFromEditBody) =>
      request<LearnFromEditResponse>('/api/style/learn', { method: 'POST', body }),
  },

  /* ---------------- connected accounts ---------------- */
  accounts: {
    /** GET /api/accounts */
    list: () => request<ListAccountsResponse>('/api/accounts'),

    /** GET /api/accounts/connect/:platform — returns an authUrl or a field spec. */
    connect: (platform: PlatformId) =>
      request<ConnectAccountResponse>(`/api/accounts/connect/${platform}`),

    /** POST /api/accounts/wordpress */
    connectWordPress: (body: ConnectWordPressBody) =>
      request<ConnectWordPressResponse>('/api/accounts/wordpress', { method: 'POST', body }),

    /** POST /api/accounts/simulated */
    createSimulated: (body: CreateSimulatedAccountBody) =>
      request<CreateSimulatedAccountResponse>('/api/accounts/simulated', {
        method: 'POST',
        body,
      }),

    /** DELETE /api/accounts/:id */
    disconnect: (accountId: string) =>
      request<DisconnectAccountResponse>(`/api/accounts/${accountId}`, { method: 'DELETE' }),

    /** GET /api/accounts/callback/:platform — the OAuth redirect target (not fetched). */
    callbackPath: (platform: PlatformId) => `/api/accounts/callback/${platform}`,
  },

  /* ---------------- media ---------------- */
  media: {
    /** POST /api/media/upload — multipart. */
    upload: (file: File, fields?: { altText?: string; source?: 'upload' | 'ai_generated' }) => {
      const form = new FormData();
      form.append('file', file);
      if (fields?.altText) form.append('altText', fields.altText);
      if (fields?.source) form.append('source', fields.source);
      return request<UploadMediaResponse>('/api/media/upload', { method: 'POST', form });
    },

    /** GET /api/media */
    list: (query: ListMediaQuery = {}) =>
      request<ListMediaResponse>('/api/media', { query: { ...query } }),

    /** GET /api/media/:id */
    get: (assetId: string) => request<GetMediaResponse>(`/api/media/${assetId}`),

    /** PATCH /api/media/:id */
    update: (assetId: string, body: UpdateMediaBody) =>
      request<UpdateMediaResponse>(`/api/media/${assetId}`, { method: 'PATCH', body }),

    /** DELETE /api/media/:id */
    remove: (assetId: string) =>
      request<DeleteMediaResponse>(`/api/media/${assetId}`, { method: 'DELETE' }),

    /** POST /api/media/:id/renditions */
    createRendition: (assetId: string, body: CreateRenditionBody) =>
      request<CreateRenditionResponse>(`/api/media/${assetId}/renditions`, {
        method: 'POST',
        body,
      }),

    /**
     * GET /api/media/file/* — use directly in `src` attributes. The workspace id
     * is appended as a query param because <img> cannot send headers.
     */
    fileUrl: (r2Key: string, workspaceId = activeWorkspaceId) =>
      `${mediaFilePath(r2Key)}${buildQuery({ workspaceId })}`,
  },

  /* ---------------- AI ---------------- */
  ai: {
    /** POST /api/ai/ideas */
    ideas: (body: AiIdeasBody = {}) =>
      request<AiIdeasResponse>('/api/ai/ideas', { method: 'POST', body }),

    /** POST /api/ai/improve */
    improve: (body: AiImproveBody) =>
      request<AiImproveResponse>('/api/ai/improve', { method: 'POST', body }),

    /** POST /api/ai/hashtags */
    hashtags: (body: AiHashtagsBody) =>
      request<AiHashtagsResponse>('/api/ai/hashtags', { method: 'POST', body }),

    /** POST /api/ai/image */
    image: (body: AiImageBody = {}) =>
      request<AiImageResponse>('/api/ai/image', { method: 'POST', body }),

    /** POST /api/ai/alt-text */
    altText: (body: AiAltTextBody) =>
      request<AiAltTextResponse>('/api/ai/alt-text', { method: 'POST', body }),
  },

  /* ---------------- posts ---------------- */
  posts: {
    /** GET /api/posts */
    list: (query: ListPostsQuery = {}) =>
      request<ListPostsResponse>('/api/posts', { query: { ...query } }),

    /** POST /api/posts */
    create: (body: CreatePostBody) =>
      request<CreatePostResponse>('/api/posts', { method: 'POST', body }),

    /** GET /api/posts/:id */
    get: (postId: string) => request<GetPostResponse>(`/api/posts/${postId}`),

    /** PATCH /api/posts/:id */
    update: (postId: string, body: UpdatePostBody) =>
      request<UpdatePostResponse>(`/api/posts/${postId}`, { method: 'PATCH', body }),

    /** DELETE /api/posts/:id */
    remove: (postId: string) =>
      request<DeletePostResponse>(`/api/posts/${postId}`, { method: 'DELETE' }),

    /** POST /api/posts/:id/generate */
    generate: (postId: string, body: GeneratePostBody) =>
      request<GeneratePostResponse>(`/api/posts/${postId}/generate`, { method: 'POST', body }),

    /** POST /api/posts/:id/schedule */
    schedule: (postId: string, body: SchedulePostBody) =>
      request<SchedulePostResponse>(`/api/posts/${postId}/schedule`, { method: 'POST', body }),

    /** POST /api/posts/:id/publish-all */
    publishAll: (postId: string) =>
      request<PublishAllResponse>(`/api/posts/${postId}/publish-all`, { method: 'POST' }),
  },

  platformPosts: {
    /** PATCH /api/platform-posts/:id */
    update: (platformPostId: string, body: UpdatePlatformPostBody) =>
      request<UpdatePlatformPostResponse>(`/api/platform-posts/${platformPostId}`, {
        method: 'PATCH',
        body,
      }),

    /** POST /api/platform-posts/:id/regenerate */
    regenerate: (platformPostId: string, body: RegeneratePlatformPostBody = {}) =>
      request<RegeneratePlatformPostResponse>(
        `/api/platform-posts/${platformPostId}/regenerate`,
        { method: 'POST', body },
      ),

    /** POST /api/platform-posts/:id/publish */
    publish: (platformPostId: string) =>
      request<PublishPlatformPostResponse>(`/api/platform-posts/${platformPostId}/publish`, {
        method: 'POST',
      }),
  },

  /* ---------------- calendar & notifications ---------------- */
  calendar: {
    /** GET /api/calendar?from&to */
    range: (query: CalendarQuery) =>
      request<CalendarResponse>('/api/calendar', { query: { ...query } }),
  },

  notifications: {
    /** GET /api/notifications */
    list: (query: ListNotificationsQuery = {}) =>
      request<ListNotificationsResponse>('/api/notifications', { query: { ...query } }),

    /** POST /api/notifications/read */
    markRead: (body: MarkNotificationsReadBody) =>
      request<MarkNotificationsReadResponse>('/api/notifications/read', {
        method: 'POST',
        body,
      }),
  },

  /* ---------------- admin & dev ---------------- */
  admin: {
    /** GET /api/admin/overview */
    overview: () => request<AdminOverviewResponse>('/api/admin/overview'),

    /** GET /api/admin/users */
    users: (query: AdminUsersQuery = {}) =>
      request<AdminUsersResponse>('/api/admin/users', { query: { ...query } }),

    /** GET /api/admin/activity */
    activity: (query: AdminActivityQuery = {}) =>
      request<AdminActivityResponse>('/api/admin/activity', { query: { ...query } }),

    /** GET /api/admin/ai-usage */
    aiUsage: (query: AdminAiUsageQuery = {}) =>
      request<AdminAiUsageResponse>('/api/admin/ai-usage', { query: { ...query } }),
  },

  dev: {
    /** POST /api/dev/seed — DEV_MODE only. */
    seed: (body: DevSeedBody = {}) =>
      request<DevSeedResponse>('/api/dev/seed', { method: 'POST', body }),
  },
} as const;

export type Api = typeof api;
export default api;
